import type { InvestmentState, InvestmentWithDetailsResponse } from "@/domain/investment";
import { isObject } from "../cache/cacheSlots";
import {
  anyOf,
  byUuid,
  distinctPages,
  isActive,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

type Row = InvestmentWithDetailsResponse;

interface InvestmentPage {
  investments: Row[];
  total_count: number;
}

export const investmentPage: ViewShape<InvestmentPage, Row, string> = {
  isPage: (value): value is InvestmentPage =>
    isObject(value) && Array.isArray(value.investments) && typeof value.total_count === "number",
  records: (current) => current.investments,
  withRecords: (current, investments, countDelta) => ({
    ...current,
    investments,
    total_count: current.total_count + countDelta,
  }),
  idOf: (record) => record.investment.uuid,
  total: (current) => current.total_count,
};

export const distinctInvestmentPages = distinctPages(investmentPage);

export const INVESTMENT_STATE_ORDER: readonly InvestmentState[] = [
  "prospect",
  "reserved",
  "financing",
  "funded",
  "declared",
  "cancelled",
  "expired",
  "rejected",
];

const amount = (row: Row) => Number(row.investment.amount);

const time = (value: string | null | undefined) => (value ? Date.parse(value) : null);

export const investmentRules: ViewRules<Row> = {
  accepts: {
    state: anyOf((row) => row.investment.state),
    amount_min: (value, row) => !isActive(value) || amount(row) >= Number(value),
    amount_max: (value, row) => !isActive(value) || amount(row) <= Number(value),
  },
  narrowing: ["search", "investor_type"],
  opaque: {
    fiscal_quota_uuid: (row) => row.investment.quota?.uuid,
    fiscal_year: (row) => row.investment.quota?.fiscal_year,
    beneficiary_uuid: (row) => row.investment.beneficiary?.uuid,
  },
  order: {
    amount,
    return_percentage: (row) => Number(row.investment.return_percentage),
    created_at: (row) => time(row.investment.created_at),
    state: (row) => INVESTMENT_STATE_ORDER.indexOf(row.investment.state),
  },
  tieBreak: byUuid("asc", (row) => row.investment.uuid),
};
